/**
 * Firebase Configuration
 * 
 * Configuration loaded from environment variables (.env file)
 * OR hardcoded as fallback
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  Auth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCredential,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';

// Web OAuth client của Firebase project vncues-5f1be (client_type 3).
// Dùng cho Google Identity Services mà KHÔNG cần Firebase Hosting.
const GOOGLE_WEB_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)
  || (window as any).VNCUES_CONFIG?.googleWebClientId
  || '49768798719-lbcg2e1smi4v3voq1fvk0va2g8e7ansm.apps.googleusercontent.com';

// Firebase Configuration - hardcoded to avoid init.json 404
const firebaseConfig = {
  apiKey: "AIzaSyBU4Wi7cwH1mhfgzf5lM1BzfH9UgZP4k5w",
  authDomain: "vncues-5f1be.firebaseapp.com",
  projectId: "vncues-5f1be",
  storageBucket: "vncues-5f1be.firebasestorage.app",
  messagingSenderId: "49768798719",
  appId: "1:49768798719:web:162a9014597b941fedefca"
};

// Validate configuration
const isConfigured = !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.authDomain);

// Initialize Firebase
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (isConfigured) {
  try {
    // Check if already initialized
app = initializeApp(firebaseConfig);
      auth = initializeAuth(app, {
        persistence: [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          browserSessionPersistence,
        ],
      });
      console.log('✅ Firebase initialized successfully');
      clearStaleRedirect();
    } catch (error: any) {
    // If already initialized, just get the auth instance
    if (error.code === 'app/duplicate-app') {
      auth = getAuth();
      console.log('✅ Firebase auth already initialized');
    } else {
      console.error('Firebase initialization error:', error);
    }
  }
}

/**
 * Xóa trạng thái redirect còn sót lại từ những lần đăng nhập Google trước.
 * Khi app không chạy trên Firebase Hosting, redirect bị lỗi (init.json 404)
 * và Firebase để lại cờ `redirectOngoing`/pending khiến các lần đăng nhập sau bị chặn.
 */
function clearStaleRedirect(): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('firebase:redirectOngoing');
    sessionStorage.removeItem('firebase:redirectOngoing');
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.indexOf('firebase:redirectResult:') === 0) keys.push(key);
    }
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.indexOf('firebase:redirectResult:') === 0) keys.push(key);
    }
    keys.forEach(k => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch {
    // Storage có thể bị chặn (private mode) - bỏ qua
  }
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

/**
 * Check if Firebase is configured
 */
export function isFirebaseConfigured(): boolean {
  return isConfigured;
}

/**
 * Đăng nhập với email/password
 */
export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  if (!auth) {
    throw new Error('Firebase chưa được cấu hình. Vui lòng cài đặt .env file.');
  }
  const result = await signInWithEmailAndPassword(auth, email, password);
  return formatUser(result.user);
}

/**
 * Đăng ký với email/password
 */
export async function signUpWithEmail(
  email: string, 
  password: string, 
  displayName?: string
): Promise<AuthUser> {
  if (!auth) {
    throw new Error('Firebase chưa được cấu hình. Vui lòng cài đặt .env file.');
  }
  const result = await createUserWithEmailAndPassword(auth, email, password);
  
  // Update profile with display name if provided
  if (displayName && result.user) {
    const { updateProfile } = await import('firebase/auth');
    await updateProfile(result.user, { displayName });
  }
  
  return formatUser(result.user);
}

/**
 * Load Google Identity Services script (accounts.google.com/gsi/client)
 */
let gisPromise: Promise<void> | null = null;
function loadGoogleIdentityServices(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google sign-in không khả dụng trên môi trường này'));
  }
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    if ((window as any).google?.accounts) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisPromise = null;
      reject(new Error('Không thể tải Google identity services. Vui lòng kiểm tra kết nối.'));
    };
    document.head.appendChild(script);
  });
  return gisPromise;
}

function resetGoogleIdentitySession(): void {
  try {
    const google = (window as any).google;
    google?.accounts?.id?.cancel?.();
    google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    // Google Identity Services may not be loaded yet.
  }
}

/**
 * Đăng nhập với Google.
 *
 * KHÔNG dùng signInWithPopup/signInWithRedirect của Firebase vì cả hai đều
 * cần Firebase Hosting phục vụ `__/auth/handler` + `__/firebase/init.json`.
 * Project vncues-5f1be không có hosting (init.json trả 404 → lỗi).
 *
 * Thay vào đó:
 * 1. Dùng Google Identity Services OAuth popup lấy access token trực tiếp.
 * 2. Đổi access token thành Firebase session bằng signInWithCredential.
 *    Đây là API client-side thuần túy, KHÔNG phụ thuộc Firebase Hosting.
 */
export async function signInWithGoogle(): Promise<AuthUser> {
  if (!auth) {
    throw new Error('Firebase chưa được cấu hình. Vui lòng cài đặt .env file.');
  }
  await loadGoogleIdentityServices();
  resetGoogleIdentitySession();

  const google = (window as any).google;
  const accessToken = await new Promise<string>((resolve, reject) => {
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_WEB_CLIENT_ID,
      scope: 'openid email profile',
      callback: (response: any) => {
        if (response?.access_token) {
          resolve(response.access_token);
        } else {
          const err = response?.error || 'Đăng nhập Google thất bại. Vui lòng thử lại.';
          reject(new Error(err));
        }
      },
      error_callback: (response: any) => {
        reject(new Error(response?.message || response?.type || 'Không mở được cửa sổ đăng nhập Google.'));
      },
    });
    tokenClient.requestAccessToken({ prompt: 'select_account' });
  });

  const result = await signInWithCredential(auth, GoogleAuthProvider.credential(null, accessToken));
  return formatUser(result.user);
}

export async function signInWithGoogleIdToken(idToken: string): Promise<AuthUser> {
  if (!auth) {
    throw new Error('Firebase chưa được cấu hình.');
  }
  const result = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
  return formatUser(result.user);
}

/**
 * Kiểm tra kết quả sau khi redirect về từ Google
 * Gọi hàm này một lần khi app mount
 */
/**
 * Đăng xuất
 */
export async function signOut(): Promise<void> {
  if (!auth) {
    throw new Error('Firebase chưa được cấu hình.');
  }
  resetGoogleIdentitySession();
  await firebaseSignOut(auth);
  resetGoogleIdentitySession();
}

/**
 * Lấy Firebase ID Token (để gửi lên WordPress)
 */
export async function getIdToken(): Promise<string | null> {
  if (!auth) return null;
  if (!auth.currentUser) {
    try {
      await auth.authStateReady();
    } catch {
      return null;
    }
  }
  if (!auth.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken();
  } catch {
    return null;
  }
}

/**
 * Lấy current user
 */
export function getCurrentFirebaseUser(): FirebaseUser | null {
  return auth?.currentUser ?? null;
}

/**
 * Listen to auth state changes
 */
export function onAuthChange(callback: (user: AuthUser | null) => void): () => void {
  if (!auth) {
    console.warn('Firebase not configured, auth state changes disabled');
    return () => {};
  }
  return onAuthStateChanged(auth, (user) => {
    callback(user ? formatUser(user) : null);
  });
}

/**
 * Format Firebase user thành AuthUser
 */
function formatUser(user: FirebaseUser): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
  };
}

export { auth };
